import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Paragraph, Spinner, useAddToast } from '@hm-group/bads-react';
import type {
    ChannelType,
    Market,
    WorkspaceData,
    DepartmentLevelKpiPlanModels,
    SaveRequest,
    HandleScaSelectionRequest,
    DeleteNonMainMarketRequest,
    CopyMainMarketRequest,
    CopyArticlePlanToAllRegionsRequest,
    PublishMetadataPerDepartment,
} from '../../models';
import { ChannelType as CT } from '../../models';
import { useAppContext } from '../../context/AppContext';
import { httpGet, httpPost } from '../../services/httpService';
import { saveWorkspaceData, publishDepartment } from '../../services/workspaceService';
import { environment } from '../../environments/environment';
import {
    WORKSPACE_DEPARTMENT_STORE_MARKET_API,
    WORKSPACE_DEPARTMENT_ONLINE_MARKET_API,
    GROUND_DEPARTMENT_API,
    HANDLE_SCA_SELECTION_API,
    DELETE_NON_MAIN_MARKET_API,
    COPY_MAIN_MARKET_API,
    COPY_ARTICLE_PLAN_TO_ALL_REGIONS_API,
    PUBLISH_METADATA_API,
} from '../../constants';
import { UNSAVED_CELLS_KEY, SAVING_IN_PROGRESS_KEY } from '../../constants/constants';
import {
    getCellsFromStorage,
    saveCellsToStorage,
    clearCellsFromStorage,
    mergeCells,
    type CellData,
} from '../../utils/localStorageHelpers';
import { Workspace } from '../Workspace/Workspace';
import { ClipboardService } from '../../services/clipboardService';
import { ConfirmationModal } from '../ConfirmationModal/ConfirmationModal';
import type { ConfirmationModalConfig } from '../ConfirmationModal/ConfirmationModal';
import type { KpiDataItem } from '../../services/copyArticleService';
import { useKpiContext } from '../../context/KpiContext';
import { useScaContext } from '../../context/ScaContext';
import { useMarketContext } from '../../context/MarketContext';
import { suppressNextError } from '../../services/httpErrorBus';
import styles from './DepartmentRegion.module.css';

const SEPARATOR = '__';

/** Check whether a single KPI copy-item has a matching non-zero value for the given target market. */
function hasMatchingDataForItem(
    item: KpiDataItem,
    marketCode: number,
    allKpiData: DepartmentLevelKpiPlanModels[],
): boolean {
    const parts = item.id.split(SEPARATOR);
    if (parts.length < 4) return false;
    const [kpiCode, , seasonKey, weekWithYear] = parts;
    const targetId = `${kpiCode}${SEPARATOR}${marketCode}${SEPARATOR}${seasonKey}${SEPARATOR}${weekWithYear}`;

    const kpiModels = allKpiData.filter((m) => m.id === kpiCode);
    return kpiModels.some((kpiModel) =>
        kpiModel.data?.some((d) => d.id === targetId && Number(d.value) !== 0),
    );
}

interface DepartmentRegionProps {
    channelType: ChannelType;
    allMarkets: Market[];
    visibleMarkets: Market[];
}

export function DepartmentRegion({
    channelType,
    allMarkets,
    visibleMarkets,
}: Readonly<DepartmentRegionProps>) {
    const { kpiSelection, isIncludeExcitementToggleChecked, isShowGroundArticlesToggleChecked } = useKpiContext();
    const { pendingScaAction, clearScaAction: onScaActionHandled } = useScaContext();
    const {
        pendingMarketAction,
        clearMarketAction: onMarketActionHandled,
        onMarketLinkCompleted,
        connectedMarketCodes,
        disconnectedMarketCodes,
        mainMarketCode,
        departmentMetadata: contextDepartmentMetadata,
    } = useMarketContext();
    const { searchBarData, selectedDepartmentInfo, publishBtnClicked, setEnablePublishBtn, lastHandledPublishClick, setLastHandledPublishClick } = useAppContext();
    const { addToast } = useAddToast();

    const isDepartmentSelected = searchBarData.showWorkspace;
    const channelLabel = channelType === CT.Store ? 'store' : 'online';
    const isStoreWorkspace = channelType === CT.Store;

    // ── Workspace data state ───────────────────────────────────────
    const [workspaceData, setWorkspaceData] = useState<WorkspaceData | null>(null);
    const [groundRowData, setGroundRowData] = useState<DepartmentLevelKpiPlanModels[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [stateGuid, setStateGuid] = useState<string>('');
    const [savingGrid, setSavingGrid] = useState<'main' | 'non-main' | null>(null);
    const [tooltipTrigger, setTooltipTrigger] = useState(0);
    const [departmentPublishInfo, setDepartmentPublishInfo] = useState<PublishMetadataPerDepartment | undefined>();
    const [isPublishing, setIsPublishing] = useState(false);
    const unsavedCellsRef = useRef<Map<string, { value: number; isLatestSeason: boolean }>>(new Map());
    const hasLoadedDataRef = useRef(false);
    const clipboardServiceRef = useRef(new ClipboardService(15));

    // ── Save recovery state ────────────────────────────────────────
    const [isGridStateChanged, setIsGridStateChanged] = useState(false);

    // ── Copy to all regions modal state ────────────────────────────
    const [showCopyModal, setShowCopyModal] = useState(false);
    const [pendingCopyData, setPendingCopyData] = useState<KpiDataItem[] | null>(null);
    const [copyTargetRegionNames, setCopyTargetRegionNames] = useState<string>('');

    /** Enable publish button when the workspace state differs from the last-published state for the current channel. */
    const updatePublishButtonState = useCallback(
        (currentStateGuid: string, publishedGuidOverride?: string) => {
            const publishedStateGuid = publishedGuidOverride ?? (isStoreWorkspace
                ? departmentPublishInfo?.storeMetadataResultModel?.stateGuid
                : departmentPublishInfo?.onlineMetadataResultModel?.stateGuid);
            setEnablePublishBtn(currentStateGuid !== publishedStateGuid);
        },
        [isStoreWorkspace, departmentPublishInfo, setEnablePublishBtn],
    );

    // Track previous dependency values for debugging
    const prevDepsRef = useRef({
        isDepartmentSelected,
        departmentId: selectedDepartmentInfo?.departmentId,
        marketKeys: '',
        isStoreWorkspace,
    });

    // ── Derive selected regions for each market type (memoized to prevent unnecessary Workspace re-renders) ───
    const mainMarketRegions = useMemo(
        () =>
            visibleMarkets
                .filter((m) => m.isMainMarket && m.channelIntegrationKey === channelType)
                .sort((a, b) => a.sortOrder - b.sortOrder),
        [visibleMarkets, channelType]
    );

    const nonMainMarketRegions = useMemo(
        () =>
            visibleMarkets
                .filter((m) => !m.isMainMarket && m.channelIntegrationKey === channelType)
                .sort((a, b) => a.sortOrder - b.sortOrder),
        [visibleMarkets, channelType]
    );


    // Create stable identifier from allMarkets to avoid unnecessary refetches on eye-toggle
    const marketKeys = useMemo(
        () => [...allMarkets].sort((a, b) => a.sortOrder - b.sortOrder).map((m) => m.marketCode).join(','),
        [allMarkets]
    );

    // ── Fetch workspace + ground data ──────────────────────────────
    useEffect(() => {
        const prev = prevDepsRef.current;
        const changes = [];

        if (prev.isDepartmentSelected !== isDepartmentSelected) {
            changes.push(`isDepartmentSelected: ${prev.isDepartmentSelected} → ${isDepartmentSelected}`);
        }
        if (prev.departmentId !== selectedDepartmentInfo?.departmentId) {
            changes.push(`departmentId: ${prev.departmentId} → ${selectedDepartmentInfo?.departmentId}`);
        }
        if (prev.marketKeys !== marketKeys) {
            changes.push(`marketKeys: ${prev.marketKeys} → ${marketKeys}`);
        }
        if (prev.isStoreWorkspace !== isStoreWorkspace) {
            changes.push(`isStoreWorkspace: ${prev.isStoreWorkspace} → ${isStoreWorkspace}`);
        }

        // Update previous values
        prevDepsRef.current = {
            isDepartmentSelected,
            departmentId: selectedDepartmentInfo?.departmentId,
            marketKeys,
            isStoreWorkspace,
        };

        if (!isDepartmentSelected || !selectedDepartmentInfo || allMarkets.length === 0) {
            setWorkspaceData(null);
            setGroundRowData([]);
            hasLoadedDataRef.current = false;
            // Disable publish button when no department selected
            setEnablePublishBtn(false);
            return;
        }

        // Don't refetch if we already have data and only MSAL instance changed
        // Only refetch on initial mount or when department/markets change
        if (hasLoadedDataRef.current && changes.length === 0) {
            return;
        }

        let cancelled = false;
        const deptId = selectedDepartmentInfo.departmentId;
        const brandId = selectedDepartmentInfo.corporateBrandId;
        const base = environment.apiConfig.articleplan_api.uri;
        const wsApi = isStoreWorkspace
            ? WORKSPACE_DEPARTMENT_STORE_MARKET_API
            : WORKSPACE_DEPARTMENT_ONLINE_MARKET_API;
        const channelId = isStoreWorkspace ? CT.Store : CT.Online;

        setEnablePublishBtn(false);
        setIsLoading(true);
        setLoadError(null);

        // Fetch ground data separately so its errors/empty state can be reported independently
        suppressNextError();
        const groundPromise = httpGet<DepartmentLevelKpiPlanModels[]>(
            `${base}${GROUND_DEPARTMENT_API}${deptId}/${channelId}/${brandId}`,
        ).catch((err) => {
            if (!cancelled) {
                const msg = err instanceof Error ? err.message : 'Unknown error';
                addToast({ title: 'Error while fetching ground data', subtitle: msg, type: 'error', timeout: 5000 });
            }
            return null;
        });

        Promise.all([
            httpGet<WorkspaceData>(`${base}${wsApi}${deptId}`),
            groundPromise,
            httpGet<PublishMetadataPerDepartment>(`${base}${PUBLISH_METADATA_API}${deptId}`),
        ])
            .then(([wsData, groundData, publishData]) => {
                if (cancelled) return;
                setWorkspaceData(wsData);

                if (groundData !== null && groundData.length === 0) {
                    addToast({ title: 'Ground data', subtitle: 'There are no data available for ground', type: 'warning', timeout: 5000 });
                }
                setGroundRowData(groundData ?? []);

                setDepartmentPublishInfo(publishData);
                // Capture stateGuid from workspace data for save operations
                setStateGuid(wsData?.stateGuid || '');
                // Clear unsaved cells on fresh load
                unsavedCellsRef.current.clear();
                // Mark that we have loaded data
                hasLoadedDataRef.current = true;
                // Enable publish button after successful data load
                const publishedGuid = isStoreWorkspace
                    ? publishData?.storeMetadataResultModel?.stateGuid
                    : publishData?.onlineMetadataResultModel?.stateGuid;
                updatePublishButtonState(wsData?.stateGuid ?? '', publishedGuid);
            })
            .catch((err) => {
                if (cancelled) return;
                setLoadError(err instanceof Error ? err.message : 'Failed to load workspace data');
                // Disable publish button on error
                setEnablePublishBtn(false);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => { cancelled = true; };
    }, [isDepartmentSelected, selectedDepartmentInfo, marketKeys, isStoreWorkspace, savingGrid, allMarkets.length, setEnablePublishBtn, addToast, updatePublishButtonState]);

    // ── Handle publish button click ───────────────────────────────────
    useEffect(() => {
        // Skip if no button click, no department selected, or already publishing
        if (publishBtnClicked === 0 || !selectedDepartmentInfo || isPublishing) return;

        // Prevent double-invocation by checking if we've already handled this click (shared across all instances)
        if (lastHandledPublishClick === publishBtnClicked) return;
        setLastHandledPublishClick(publishBtnClicked);

        const handlePublish = async () => {
            setIsPublishing(true);
            try {
                suppressNextError();
                const response = await publishDepartment(selectedDepartmentInfo.departmentId);

                // Update last published info with new data
                // Preserve existing metadata fields while updating publish-specific fields
                setDepartmentPublishInfo((prev) => ({
                    storeMetadataResultModel: {
                        stateGuid: response.storeStateGuid,
                        publishedTimeUtc: response.storePublishedTimeUtc,
                        publishedBy: response.storePublishedBy,
                        departmentId: selectedDepartmentInfo.departmentId,
                        channelId: CT.Store,
                        isSuccess: true,
                        // Preserve existing metadata or use current component state
                        isCoreExtendedChecked: prev?.storeMetadataResultModel?.isCoreExtendedChecked ?? false,
                        isExcitementChecked: prev?.storeMetadataResultModel?.isExcitementChecked ?? false,
                        selectedMarkets: prev?.storeMetadataResultModel?.selectedMarkets ?? visibleMarkets,
                    },
                    onlineMetadataResultModel: {
                        stateGuid: response.onlineStateGuid,
                        publishedTimeUtc: response.onlinePublishedTimeUtc,
                        publishedBy: response.onlinePublishedBy,
                        departmentId: selectedDepartmentInfo.departmentId,
                        channelId: CT.Online,
                        isSuccess: true,
                        // Preserve existing metadata or use current component state
                        isCoreExtendedChecked: prev?.onlineMetadataResultModel?.isCoreExtendedChecked ?? false,
                        isExcitementChecked: prev?.onlineMetadataResultModel?.isExcitementChecked ?? false,
                        selectedMarkets: prev?.onlineMetadataResultModel?.selectedMarkets ?? visibleMarkets,
                    },
                }));

                // Show success toast
                addToast({
                    title: 'Published successfully for the department',
                    subtitle: '',
                    type: 'success',
                    timeout: 5000,
                });

                // Hide button after successful publish if state matches published state
                const publishedStateGuid = isStoreWorkspace ? response.storeStateGuid : response.onlineStateGuid;
                updatePublishButtonState(stateGuid, publishedStateGuid);

            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : 'Unknown error';
                addToast({
                    title: 'Error publishing plan',
                    subtitle: errorMessage,
                    type: 'error',
                    timeout: 5000,
                });
                // Keep button visible on error
            } finally {
                setIsPublishing(false);
            }
        };

        handlePublish();
    }, [publishBtnClicked, selectedDepartmentInfo, isPublishing, lastHandledPublishClick, setLastHandledPublishClick, visibleMarkets, addToast, isStoreWorkspace, stateGuid, setEnablePublishBtn, updatePublishButtonState]);

    // ── Handle tooltip trigger (double-click on cell) ─────────────
    const handleTooltipTrigger = useCallback(() => {
        setTooltipTrigger(prev => prev + 1);
    }, []);

    // ── Handle cell changes from workspace ─────────────────────────
    const handleWeekDataChangedRef = useRef<((data: Map<string, { value: number; isLatestSeason: boolean }>, isMainMarket: boolean) => Promise<void>) | undefined>(undefined);

    /** Merge in-progress and unsaved cells back to unsaved storage (used on conflict or error). */
    const mergeBackToUnsaved = useCallback((deptId: number, channelId: number) => {
        const inProgressCells = getCellsFromStorage(SAVING_IN_PROGRESS_KEY, deptId, channelId);
        const unsavedCells = getCellsFromStorage(UNSAVED_CELLS_KEY, deptId, channelId);
        const merged = mergeCells(inProgressCells, unsavedCells);
        saveCellsToStorage(UNSAVED_CELLS_KEY, deptId, channelId, merged);
        clearCellsFromStorage(SAVING_IN_PROGRESS_KEY);
    }, []);

    /** Handle state-conflict response: merge back cells, show recovery banner, refetch workspace. */
    const handleStateConflict = useCallback((deptId: number, channelId: number) => {
        mergeBackToUnsaved(deptId, channelId);
        setIsGridStateChanged(true);
        setEnablePublishBtn(false);

        const brandId = selectedDepartmentInfo?.corporateBrandId ?? '';
        const base = environment.apiConfig.articleplan_api.uri;
        const wsApi = isStoreWorkspace
            ? WORKSPACE_DEPARTMENT_STORE_MARKET_API
            : WORKSPACE_DEPARTMENT_ONLINE_MARKET_API;

        Promise.all([
            httpGet<WorkspaceData>(`${base}${wsApi}${deptId}`),
            httpGet<DepartmentLevelKpiPlanModels[]>(
                `${base}${GROUND_DEPARTMENT_API}${deptId}/${channelId}/${brandId}`,
            ),
        ])
            .then(([wsData, groundData]) => {
                setWorkspaceData(wsData);
                setGroundRowData(groundData ?? []);
                setStateGuid(wsData?.stateGuid || '');
            })
            .catch((err) => {
                console.error('Error refetching workspace after state change:', err);
            });
    }, [mergeBackToUnsaved, selectedDepartmentInfo, isStoreWorkspace, setEnablePublishBtn]);

    /** Handle successful (no-conflict) save: update state, auto-save remaining cells, refresh non-main. */
    const handleSuccessfulSave = useCallback(
        (newStateGuid: string, deptId: number, channelId: number, isMainMarket: boolean) => {
            clearCellsFromStorage(SAVING_IN_PROGRESS_KEY);
            unsavedCellsRef.current.clear();
            setStateGuid(newStateGuid);

            // Check if more cells were added while saving
            const remainingUnsaved = getCellsFromStorage(UNSAVED_CELLS_KEY, deptId, channelId);
            if (remainingUnsaved.length > 0 && !isGridStateChanged) {
                console.log('More cells edited during save, continuing...');
                const remainingMap = new Map(
                    remainingUnsaved.map(c => [c.id, { value: c.value, isLatestSeason: c.isLatestSeason }])
                );
                handleWeekDataChangedRef.current?.(remainingMap, isMainMarket);
                return;
            }

            // Enable publish button if state differs from published state
            updatePublishButtonState(newStateGuid);

            // Refetch workspace data so regionHasAnyExistingData reflects the latest saved values.
            // When main market saves, the backend also propagates to connected non-main markets.
            // When non-main market saves, workspaceData must be refreshed so the copy-to-all-regions
            // popup can detect which markets now have non-zero data.
            if (isMainMarket ? nonMainMarketRegions.length > 0 : true) {
                const base = environment.apiConfig.articleplan_api.uri;
                const wsApi = isStoreWorkspace
                    ? WORKSPACE_DEPARTMENT_STORE_MARKET_API
                    : WORKSPACE_DEPARTMENT_ONLINE_MARKET_API;
                httpGet<WorkspaceData>(`${base}${wsApi}${deptId}`)
                    .then((wsData) => {
                        setWorkspaceData(wsData);
                        setStateGuid(wsData?.stateGuid ?? '');
                    })
                    .catch((err) => {
                        console.error('Failed to refresh workspace after save:', err);
                    });
            }
        },
        [isStoreWorkspace, nonMainMarketRegions, isGridStateChanged, updatePublishButtonState],
    );

    const handleWeekDataChanged = useCallback(
        async (data: Map<string, { value: number; isLatestSeason: boolean }>, isMainMarket: boolean) => {
            if (!selectedDepartmentInfo || savingGrid !== null) {
                return;
            }

            const deptId = selectedDepartmentInfo.departmentId;
            const channelId = isStoreWorkspace ? CT.Store : CT.Online;

            // Merge new changes with any pending unsaved cells
            data.forEach((value, key) => {
                unsavedCellsRef.current.set(key, value);
            });

            // Build save request from all unsaved cells
            const saveData: CellData[] = [];
            unsavedCellsRef.current.forEach((value, key) => {
                saveData.push({
                    id: key,
                    value: value.value,
                    isLatestSeason: value.isLatestSeason,
                });
            });

            // Persist to localStorage before starting save
            saveCellsToStorage(UNSAVED_CELLS_KEY, deptId, channelId, saveData);

            // Move from unsaved to in-progress before API call
            saveCellsToStorage(SAVING_IN_PROGRESS_KEY, deptId, channelId, saveData);
            clearCellsFromStorage(UNSAVED_CELLS_KEY);

            const requestBody: SaveRequest = {
                channelId,
                stateGuid: stateGuid,
                data: saveData,
            };

            try {
                setSavingGrid(isMainMarket ? 'main' : 'non-main');
                const response = await saveWorkspaceData(deptId, requestBody);

                if (response.isStateChanged) {
                    console.warn('State changed during save, enabling recovery mode...');
                    handleStateConflict(deptId, channelId);
                } else {
                    handleSuccessfulSave(response.newStateGuid, deptId, channelId, isMainMarket);
                }
            } catch (error) {
                console.error('Error saving workspace data:', error);
                addToast({
                    title: 'Save failed',
                    subtitle: 'Unable to save changes. Your edits have been preserved.',
                    type: 'error',
                    timeout: 5000
                });
                mergeBackToUnsaved(deptId, channelId);
            } finally {
                setSavingGrid(null);
            }
        },
        [
            selectedDepartmentInfo,
            isStoreWorkspace,
            stateGuid,
            savingGrid,
            addToast,
            handleStateConflict,
            handleSuccessfulSave,
            mergeBackToUnsaved,
        ],
    );

    // Keep ref in sync for recursive calls from handleSuccessfulSave
    useEffect(() => { handleWeekDataChangedRef.current = handleWeekDataChanged; }, [handleWeekDataChanged]);

    // ── Handle recovery confirmation after grid state change ───────
    const handleConfirmReapply = useCallback(async () => {
        if (!selectedDepartmentInfo) return;

        const deptId = selectedDepartmentInfo.departmentId;
        const channelId = isStoreWorkspace ? CT.Store : CT.Online;
        const brandId = selectedDepartmentInfo.corporateBrandId;

        // Merge all pending cells from localStorage
        const inProgressCells = getCellsFromStorage(SAVING_IN_PROGRESS_KEY, deptId, channelId);
        const unsavedCells = getCellsFromStorage(UNSAVED_CELLS_KEY, deptId, channelId);
        const merged = mergeCells(inProgressCells, unsavedCells);

        if (merged.length === 0) {
            console.warn('No cells to re-apply');
            setIsGridStateChanged(false);
            return;
        }

        // Clear recovery banner immediately
        setIsGridStateChanged(false);

        // Build save request - match Angular's behavior: save the data but ignore response
        const requestBody: SaveRequest = {
            channelId,
            stateGuid: stateGuid,
            data: merged,
        };

        const base = environment.apiConfig.articleplan_api.uri;
        const wsApi = isStoreWorkspace
            ? WORKSPACE_DEPARTMENT_STORE_MARKET_API
            : WORKSPACE_DEPARTMENT_ONLINE_MARKET_API;

        try {
            // Save the merged cells (ignore response, just like Angular does with reload=true)
            await saveWorkspaceData(deptId, requestBody);

            // Clear localStorage after save attempt
            clearCellsFromStorage(SAVING_IN_PROGRESS_KEY);
            clearCellsFromStorage(UNSAVED_CELLS_KEY);

            // Explicitly refetch workspace data (matching Angular's getWorkspaceData call)
            const [wsData, groundData] = await Promise.all([
                httpGet<WorkspaceData>(`${base}${wsApi}${deptId}`),
                httpGet<DepartmentLevelKpiPlanModels[]>(
                    `${base}${GROUND_DEPARTMENT_API}${deptId}/${channelId}/${brandId}`,
                ),
            ]);

            setWorkspaceData(wsData);
            setGroundRowData(groundData ?? []);
            setStateGuid(wsData?.stateGuid || '');
        } catch (error) {
            console.error('Error during reapply save:', error);
            // Even on error, clear localStorage and refetch
            clearCellsFromStorage(SAVING_IN_PROGRESS_KEY);
            clearCellsFromStorage(UNSAVED_CELLS_KEY);

            // Try to refetch workspace even after error
            try {
                const [wsData, groundData] = await Promise.all([
                    httpGet<WorkspaceData>(`${base}${wsApi}${deptId}`),
                    httpGet<DepartmentLevelKpiPlanModels[]>(
                        `${base}${GROUND_DEPARTMENT_API}${deptId}/${channelId}/${brandId}`,
                    ),
                ]);

                setWorkspaceData(wsData);
                setGroundRowData(groundData ?? []);
                setStateGuid(wsData?.stateGuid || '');
            } catch (refetchError) {
                console.error('Error refetching workspace:', refetchError);
            }
        }
    }, [selectedDepartmentInfo, isStoreWorkspace, stateGuid]);

    // ── React to SCA actions from selection bar ─────────────────────
    useEffect(() => {
        if (!pendingScaAction || !selectedDepartmentInfo) return;

        const deptId = selectedDepartmentInfo.departmentId;
        const base = environment.apiConfig.articleplan_api.uri;
        const stateGuid = workspaceData?.stateGuid ?? '';

        const requestBody: HandleScaSelectionRequest = {
            channel: channelType,
            scaActionType: pendingScaAction.scaActionType,
            departmentMetadata: {
                departmentId: Number(deptId),
                channelId: channelType,
                isCoreExtendedChecked: pendingScaAction.isCoreExtendedChecked,
                isExcitementChecked: pendingScaAction.isExcitementChecked,
                includeExcitementInSummary: pendingScaAction.includeExcitementInSummary,
                selectedMarkets: contextDepartmentMetadata.selectedMarkets,
                visibleMarkets,
                stateGuid,
            },
        };

        setIsLoading(true);
        const brandId = selectedDepartmentInfo.corporateBrandId;
        const channelId = isStoreWorkspace ? CT.Store : CT.Online;

        httpPost<HandleScaSelectionRequest, WorkspaceData>(
            `${base}${HANDLE_SCA_SELECTION_API}${deptId}`,
            requestBody,
        )
            .then((resp) => {
                if (resp.isStateChanged) {
                    console.warn('State changed on server. Please refresh.');
                }
                setWorkspaceData(resp);
                setStateGuid(resp.stateGuid ?? '');
                //implement publish button state after SCA action
                updatePublishButtonState(resp.stateGuid ?? '');

                // Check for in-progress save cells after workspace replacement
                const inProgressCells = getCellsFromStorage(SAVING_IN_PROGRESS_KEY, deptId, channelId);
                if (inProgressCells.length > 0) {
                    console.log('Detected in-progress save during SCA action, merging to unsaved...');
                    const unsavedCells = getCellsFromStorage(UNSAVED_CELLS_KEY, deptId, channelId);
                    const merged = mergeCells(inProgressCells, unsavedCells);

                    saveCellsToStorage(UNSAVED_CELLS_KEY, deptId, channelId, merged);
                    clearCellsFromStorage(SAVING_IN_PROGRESS_KEY);

                    setIsGridStateChanged(true);
                }

                // Refresh ground data
                return httpGet<DepartmentLevelKpiPlanModels[]>(
                    `${base}${GROUND_DEPARTMENT_API}${deptId}/${channelId}/${brandId}`,
                );
            })
            .then((groundData) => {
                setGroundRowData(groundData ?? []);
            })
            .catch((err) => {
                console.error('SCA selection failed:', err);
                setLoadError(err instanceof Error ? err.message : 'SCA action failed');
            })
            .finally(() => {
                setIsLoading(false);
                onScaActionHandled?.();
            });

        // Intentionally react only to pendingScaAction changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingScaAction]);

    // ── React to market connect action from selection bar ──────────
    // Connect = Link to main market = Remove independent data (market will mirror main market, read-only)
    useEffect(() => {
        if (pendingMarketAction?.actionType !== 'connect' || !selectedDepartmentInfo) return;

        const deptId = selectedDepartmentInfo.departmentId;
        const base = environment.apiConfig.articleplan_api.uri;
        const brandId = selectedDepartmentInfo.corporateBrandId;
        const channelId = isStoreWorkspace ? CT.Store : CT.Online;
        // Use latest stateGuid from component state (line 101), not from workspaceData which may be stale

        // Connect = delete independent data → market should be REMOVED from disconnected list
        const selectedDisconnectedMarketRegions = allMarkets.filter(
            (m) => m.channelIntegrationKey === channelType &&
                (m.isMainMarket || (disconnectedMarketCodes.has(m.marketCode) && m.marketCode !== pendingMarketAction.marketCode)),
        );
        // Use departmentMetadata from context, overriding only the fields that differ locally
        const departmentMetadata = {
            ...contextDepartmentMetadata,
            selectedMarkets: selectedDisconnectedMarketRegions,
            visibleMarkets,
            stateGuid, // Use component state variable, not workspaceData.stateGuid
        };

        setIsLoading(true);

        // Delete non-main market independent data (so market mirrors main market)
        const deleteRequest: DeleteNonMainMarketRequest = {
            channelId: channelType,
            nonMainMarketid: pendingMarketAction.marketCode,
            departmentMetadata,
        };

        httpPost<DeleteNonMainMarketRequest, WorkspaceData>(
            `${base}${DELETE_NON_MAIN_MARKET_API}${deptId}`,
            deleteRequest,
        )
            .then((deleteResp) => {
                // Update market context FIRST
                onMarketLinkCompleted?.(pendingMarketAction.marketCode, 'connect');
                // Check for state conflict in delete response
                if (deleteResp.isStateChanged) {
                    console.warn('State changed during market connect, triggering recovery...');
                    handleStateConflict(deptId, channelId);
                    addToast({
                        title: 'Workspace data changed',
                        subtitle: 'Grid reloaded. Your connect operation completed.',
                        type: 'warning',
                        timeout: 5000
                    });
                    return;
                }
                // No state conflict - refetch complete workspace data
                const wsApi = isStoreWorkspace
                    ? WORKSPACE_DEPARTMENT_STORE_MARKET_API
                    : WORKSPACE_DEPARTMENT_ONLINE_MARKET_API;
                return Promise.all([
                    httpGet<WorkspaceData>(`${base}${wsApi}${deptId}`),
                    httpGet<DepartmentLevelKpiPlanModels[]>(
                        `${base}${GROUND_DEPARTMENT_API}${deptId}/${channelId}/${brandId}`,
                    ),
                ]);
            })
            .then((result) => {
                if (!result) return; // Skip if state conflict was handled
                const [wsData, groundData] = result;
                setWorkspaceData(wsData);
                setGroundRowData(groundData ?? []);
                setStateGuid(wsData?.stateGuid || '');
                //enable publish button after connect action
                updatePublishButtonState(wsData.stateGuid);
            })
            .catch((err) => {
                console.error('Market connect failed:', err);
                setLoadError(err instanceof Error ? err.message : 'Market connect failed');
            })
            .finally(() => {
                setIsLoading(false);
                onMarketActionHandled?.();
            });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingMarketAction]);

    // ── React to market disconnect action from selection bar ──────────
    // Disconnect = Unlink from main market = Copy main market data to create independent editable data
    useEffect(() => {
        if (pendingMarketAction?.actionType !== 'disconnect' || !selectedDepartmentInfo) return;

        const deptId = selectedDepartmentInfo.departmentId;
        const base = environment.apiConfig.articleplan_api.uri;
        const brandId = selectedDepartmentInfo.corporateBrandId;
        const channelId = isStoreWorkspace ? CT.Store : CT.Online;
        // Use latest stateGuid from component state (line 101), not from workspaceData which may be stale

        const selectedDisconnectedMarketRegions = allMarkets.filter(
            (m) => m.channelIntegrationKey === channelType &&
                (m.isMainMarket || disconnectedMarketCodes.has(m.marketCode) || m.marketCode === pendingMarketAction.marketCode),
        );
        // Use departmentMetadata from context, overriding only the fields that differ locally
        const departmentMetadata = {
            ...contextDepartmentMetadata,
            selectedMarkets: selectedDisconnectedMarketRegions,
            visibleMarkets,
            stateGuid, // Use component state variable, not workspaceData.stateGuid
        };

        setIsLoading(true);

        // Copy main market data to create independent editable data
        // Backend CopyMainMarketKpiToNonMainMarket will update existing data or add if not present
        const copyRequest: CopyMainMarketRequest = {
            channelId: channelType,
            mainMarketid: pendingMarketAction.mainMarketCode,
            nonMainMarketid: [pendingMarketAction.marketCode],
            departmentMetadata,
        };

        httpPost<CopyMainMarketRequest, WorkspaceData>(
            `${base}${COPY_MAIN_MARKET_API}${deptId}`,
            copyRequest,
        )
            .then((copyResp) => {
                // Update market context FIRST
                onMarketLinkCompleted?.(pendingMarketAction.marketCode, 'disconnect');
                // Check for state conflict in copy response
                if (copyResp.isStateChanged) {
                    console.warn('State changed during market disconnect, triggering recovery...');
                    handleStateConflict(deptId, channelId);
                    addToast({
                        title: 'Workspace data changed',
                        subtitle: 'Grid reloaded. Your disconnect operation completed.',
                        type: 'warning',
                        timeout: 5000
                    });
                    return;
                }
                // No state conflict - refetch complete workspace data
                const wsApi = isStoreWorkspace
                    ? WORKSPACE_DEPARTMENT_STORE_MARKET_API
                    : WORKSPACE_DEPARTMENT_ONLINE_MARKET_API;
                return Promise.all([
                    httpGet<WorkspaceData>(`${base}${wsApi}${deptId}`),
                    httpGet<DepartmentLevelKpiPlanModels[]>(
                        `${base}${GROUND_DEPARTMENT_API}${deptId}/${channelId}/${brandId}`,
                    ),
                ]);
            })
            .then((result) => {
                if (!result) return; // Skip if state conflict was handled
                const [wsData, groundData] = result;
                setWorkspaceData(wsData);
                setGroundRowData(groundData ?? []);
                setStateGuid(wsData?.stateGuid || '');
                //enable publish button after disconnect action
                updatePublishButtonState(wsData.stateGuid);
            })
            .catch((err) => {
                console.error('Market disconnect failed:', err);
                setLoadError(err instanceof Error ? err.message : 'Market disconnect failed');
            })
            .finally(() => {
                setIsLoading(false);
                onMarketActionHandled?.();
            });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingMarketAction]);

    // ── Helper: check if a region has existing non-zero data for the exact KPI + season + week being copied ──
    const regionHasAnyExistingData = useCallback(
        (marketCode: number, kpiData: KpiDataItem[]): boolean => {
            const allKpiData = workspaceData?.departmentLevelKpiPlanModels ?? [];
            return kpiData.some((item) => hasMatchingDataForItem(item, marketCode, allKpiData));
        },
        [workspaceData],
    );

    // ── Handle "copy cells to non-main market" from Workspace - shows confirmation modal ──────
    const handleCopyToAllRegions = useCallback(
        (kpiData: KpiDataItem[]) => {
            if (!selectedDepartmentInfo) return;

            const mainMarket = allMarkets.find((m) => m.isMainMarket && m.channelIntegrationKey === channelType);
            if (!mainMarket) {
                addToast({ title: 'Error', subtitle: 'Main market not found', type: 'error', timeout: 5000 });
                return;
            }

            // Get disconnected non-main markets (markets with independent data that will be overwritten)
            const disconnectedNonMainMarkets = allMarkets.filter(
                (m) => !m.isMainMarket && m.channelIntegrationKey === channelType && disconnectedMarketCodes.has(m.marketCode),
            );

            // Find disconnected regions that have existing data for the exact KPI + season + week being copied
            const regionsWithData = disconnectedNonMainMarkets.filter((region) =>
                regionHasAnyExistingData(region.marketCode, kpiData),
            );

            // Build display names for regions that will be affected
            const names = regionsWithData.map((r) => r.marketShortName);
            const formattedNames =
                names.length > 1 ? names.slice(0, -1).join(', ') + ' and ' + names.at(-1) : names.join('');

            setCopyTargetRegionNames(formattedNames);
            setPendingCopyData(kpiData);
            setShowCopyModal(true);
        },
        [selectedDepartmentInfo, allMarkets, channelType, disconnectedMarketCodes, addToast, regionHasAnyExistingData],
    );

    // ── Execute the copy to all regions API call (after confirmation) ──────
    const executeCopyToAllRegions = useCallback(
        async (kpiData: KpiDataItem[]) => {
            if (!selectedDepartmentInfo) return;

            const mainMarket = allMarkets.find((m) => m.isMainMarket && m.channelIntegrationKey === channelType);
            if (!mainMarket) {
                addToast({ title: 'Error', subtitle: 'Main market not found', type: 'error', timeout: 5000 });
                return;
            }

            const base = environment.apiConfig.articleplan_api.uri;
            const deptId = selectedDepartmentInfo.departmentId;
            const requestBody: CopyArticlePlanToAllRegionsRequest = {
                channelId: isStoreWorkspace ? CT.Store : CT.Online,
                mainMarketId: mainMarket.marketCode,
                departmentId: deptId,
                stateGuid: stateGuid,
                data: kpiData,
            };

            try {
                suppressNextError();
                const resp = await httpPost<CopyArticlePlanToAllRegionsRequest, WorkspaceData>(
                    `${base}${COPY_ARTICLE_PLAN_TO_ALL_REGIONS_API}${deptId}`,
                    requestBody,
                );

                if (resp.isStateChanged) {
                    addToast({ title: 'Warning', subtitle: 'State changed. Please refresh the page.', type: 'warning', timeout: 5000 });
                }

                setWorkspaceData(resp);
                setStateGuid(resp.stateGuid || '');
                hasLoadedDataRef.current = true;

                // Refresh ground data
                const brandId = selectedDepartmentInfo.corporateBrandId;
                const channelId = isStoreWorkspace ? CT.Store : CT.Online;
                const groundData = await httpGet<DepartmentLevelKpiPlanModels[]>(
                    `${base}${GROUND_DEPARTMENT_API}${deptId}/${channelId}/${brandId}`,
                );
                setGroundRowData(groundData ?? []);

                if (!resp.isStateChanged) {
                    addToast({ title: 'Success', subtitle: 'Successfully copied article plan to all regions', type: 'success', timeout: 5000 });

                    updatePublishButtonState(resp.stateGuid ?? '');
                }
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : 'Unknown error';
                addToast({ title: 'Error', subtitle: `Copy to all regions failed: ${errorMessage}`, type: 'error', timeout: 5000 });
            }
        },
        [selectedDepartmentInfo, allMarkets, channelType, isStoreWorkspace, stateGuid, addToast, updatePublishButtonState],
    );

    // ── Copy confirmation modal handlers ──────
    const handleCopyConfirmationYes = useCallback(() => {
        setShowCopyModal(false);
        if (pendingCopyData) {
            executeCopyToAllRegions(pendingCopyData);
        }
        setPendingCopyData(null);
        setCopyTargetRegionNames('');
    }, [pendingCopyData, executeCopyToAllRegions]);

    const handleCopyConfirmationNo = useCallback(() => {
        setShowCopyModal(false);
        setPendingCopyData(null);
        setCopyTargetRegionNames('');
    }, []);

    // ── Copy modal configuration ──────
    const copyModalConfig: ConfirmationModalConfig = useMemo(
        () => ({
            isOpen: showCopyModal,
            header: 'Copy Article Plan to All Regions',
            message: copyTargetRegionNames
                ? `Do you want to copy the article plan to all regions? Please note that this will overwrite the existing plans for ${copyTargetRegionNames}.`
                : 'Do you want to copy the article plan to all regions?',
            actions: [
                { label: 'Yes, Copy', mode: 'primary', type: 'solid', onClick: handleCopyConfirmationYes },
                { label: 'Cancel', mode: 'secondary', type: 'solid', onClick: handleCopyConfirmationNo },
            ],
            onClose: handleCopyConfirmationNo,
        }),
        [showCopyModal, copyTargetRegionNames, handleCopyConfirmationYes, handleCopyConfirmationNo],
    );

    // ── Render states ──────────────────────────────────────────────

    // No department selected
    if (!isDepartmentSelected) {
        return (
            <div className={styles.initialDisplayArea}>
                <Paragraph>
                    Please select a combination to load its {channelLabel} department level Article Plan!
                </Paragraph>
            </div>
        );
    }

    // Department selected but no valid main market
    if (allMarkets.length === 0) {
        return (
            <div className={styles.initialDisplayArea}>
                <Paragraph>
                    Please select a combination with valid main market for the {channelLabel} department level Article Plan!
                </Paragraph>
            </div>
        );
    }

    // Loading
    if (isLoading) {
        return (
            <div className={styles.initialDisplayArea}>
                <Spinner size="small" />
                <Paragraph>
                    Loading {channelLabel} department plan...
                </Paragraph>
            </div>
        );
    }

    // Error
    if (loadError) {
        return (
            <div className={styles.initialDisplayArea}>
                <Paragraph>Error: {loadError}</Paragraph>
            </div>
        );
    }

    // Data loaded – render workspace(s)
    return (
        <div>
            {/* Main market workspace */}
            {mainMarketRegions.length > 0 && (
                <div className="sticky-workspace">
                    <Workspace
                        initialGridData={workspaceData}
                        groundRowData={groundRowData}
                        readonlyPlan={false}
                        isMainMarket
                        isStoreWorkspaceTab={isStoreWorkspace}
                        selectedDepartmentInfo={selectedDepartmentInfo}
                        selectedRegions={mainMarketRegions}
                        selectedPlanKpis={kpiSelection.selectedPlanKpis}
                        selectedGroundKpis={kpiSelection.selectedGroundKpis}
                        kpiConfig={kpiSelection.kpiConfig}
                        groundKpiConfig={kpiSelection.groundKpiConfig}
                        isIncludeExcitementToggleChecked={isIncludeExcitementToggleChecked}
                        isShowGroundArticlesToggleChecked={isShowGroundArticlesToggleChecked}
                        tooltipTrigger={tooltipTrigger}
                        onTooltipTriggered={handleTooltipTrigger}
                        onWeekDataChanged={(data) => handleWeekDataChanged(data, true)}
                        showPublishedInfo={true}
                        departmentLastPublishedInfo={departmentPublishInfo}
                        isSaving={savingGrid === 'main'}
                        clipboardService={clipboardServiceRef.current}
                        onCopyToAllRegions={handleCopyToAllRegions}
                        isGridStateChanged={isGridStateChanged}
                        onConfirmReapply={handleConfirmReapply}
                    />
                </div>
            )}

            {/* Non-main market workspace */}
            {nonMainMarketRegions.length > 0 && (
                <div className="scrollable-workspaces">
                    <Workspace
                        initialGridData={workspaceData}
                        groundRowData={groundRowData}
                        readonlyPlan={false}
                        isMainMarket={false}
                        isStoreWorkspaceTab={isStoreWorkspace}
                        selectedDepartmentInfo={selectedDepartmentInfo}
                        selectedRegions={nonMainMarketRegions}
                        connectedMarketCodes={connectedMarketCodes}
                        mainMarketCode={mainMarketCode}
                        selectedPlanKpis={kpiSelection.selectedPlanKpis}
                        selectedGroundKpis={kpiSelection.selectedGroundKpis}
                        kpiConfig={kpiSelection.kpiConfig}
                        groundKpiConfig={kpiSelection.groundKpiConfig}
                        isIncludeExcitementToggleChecked={isIncludeExcitementToggleChecked}
                        isShowGroundArticlesToggleChecked={isShowGroundArticlesToggleChecked}
                        tooltipTrigger={tooltipTrigger}
                        onTooltipTriggered={handleTooltipTrigger}
                        onWeekDataChanged={(data) => handleWeekDataChanged(data, false)}
                        showPublishedInfo={true}
                        departmentLastPublishedInfo={departmentPublishInfo}
                        isSaving={savingGrid === 'non-main'}
                        clipboardService={clipboardServiceRef.current}
                    />
                </div>
            )}

            {/* Copy to all regions confirmation modal */}
            <ConfirmationModal config={copyModalConfig} />
        </div>
    );
}
